import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { ShieldCheck, Search, Filter, Clock, User, AlertTriangle, LogIn, LogOut, Key,
  UserPlus, UserMinus, Shield, ChevronDown, ChevronUp, RefreshCw, Download } from 'lucide-react';
import { NEU } from '../neuStyles';
import PrintButton from '../components/PrintButton';

const ACTION_CATEGORIES = {
  'Authentication': ['LOGIN_SUCCESS', 'LOGOUT', 'LOGIN_FAILED', 'SESSION_TIMEOUT', 'SESSION_EXPIRED'],
  'User Management': ['USER_CREATED', 'USER_UPDATED', 'USER_DEACTIVATED', 'USER_REACTIVATED', 'PASSWORD_RESET', 'PASSWORD_CHANGED'],
  'Access Control': ['ACCESS_DENIED', 'PERMISSION_CHANGED', 'ROLE_CHANGED'],
  'Emergency Access': ['BREAK_THE_GLASS', 'EMERGENCY_ACCESS_USED', 'EMERGENCY_ACCESS_EXPIRED'],
  'PHI Access': ['VIEW_LIST', 'VIEW_DETAIL', 'CREATE', 'UPDATE', 'DELETE', 'EXPORT', 'VIEW_PHI_LOG'],
  'System': ['SETTINGS_UPDATED', 'SYSTEM_CONFIG', 'DEACTIVATE', 'REACTIVATE'],
};

const ACTION_ICONS = {
  LOGIN_SUCCESS: LogIn, LOGOUT: LogOut, LOGIN_FAILED: AlertTriangle,
  SESSION_TIMEOUT: Clock, USER_CREATED: UserPlus, USER_DEACTIVATED: UserMinus,
  ACCESS_DENIED: Shield, BREAK_THE_GLASS: AlertTriangle, PASSWORD_RESET: Key,
};

const ACTION_COLORS = {
  LOGIN_SUCCESS: '#16a34a', LOGOUT: '#6b7280', LOGIN_FAILED: '#dc2626',
  ACCESS_DENIED: '#dc2626', BREAK_THE_GLASS: '#ea580c', EMERGENCY_ACCESS_USED: '#ea580c',
  USER_CREATED: '#2E8B98', USER_DEACTIVATED: '#dc2626', USER_REACTIVATED: '#16a34a',
  SESSION_TIMEOUT: '#d97706', PASSWORD_RESET: '#7c3aed',
};

const SEVERITY = {
  BREAK_THE_GLASS: 'critical', EMERGENCY_ACCESS_USED: 'critical',
  ACCESS_DENIED: 'warning', LOGIN_FAILED: 'warning',
  USER_DEACTIVATED: 'warning', PASSWORD_RESET: 'info',
  USER_CREATED: 'info', LOGIN_SUCCESS: 'success', LOGOUT: 'neutral',
};

export default function AdminAuditPage({ user }) {
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [limit, setLimit] = useState(200);
  const [sortDir, setSortDir] = useState('desc');
  const [activeTab, setActiveTab] = useState('audit');
  const [loginHistory, setLoginHistory] = useState([]);
  const [phiLog, setPhiLog] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [auditResult, phiResult] = await Promise.all([
        window.zignagenixAPI.getAuditLog(limit),
        window.zignagenixAPI.getPhiAccessLog(limit),
      ]);
      const auditItems = Array.isArray(auditResult) ? auditResult : (auditResult?.data || []);
      setAuditLog(auditItems);

      // Extract login events for login history tab
      const logins = auditItems.filter(e =>
        ['LOGIN_SUCCESS', 'LOGIN_FAILED', 'LOGOUT', 'SESSION_TIMEOUT'].includes(e.action)
      );
      setLoginHistory(logins);

      const phiItems = Array.isArray(phiResult) ? phiResult : (phiResult?.data || []);
      setPhiLog(phiItems);
    } catch (err) {
      console.error('Error fetching audit data:', err);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const uniqueUsers = useMemo(() => {
    const set = new Set();
    auditLog.forEach(e => { if (e.user_name) set.add(e.user_name); });
    return [...set].sort();
  }, [auditLog]);

  const uniqueActions = useMemo(() => {
    const set = new Set();
    auditLog.forEach(e => set.add(e.action));
    return [...set].sort();
  }, [auditLog]);

  const filteredLog = useMemo(() => {
    let data = auditLog;
    if (actionFilter !== 'all') {
      // Check if it's a category
      const categoryActions = ACTION_CATEGORIES[actionFilter];
      if (categoryActions) {
        data = data.filter(e => categoryActions.includes(e.action));
      } else {
        data = data.filter(e => e.action === actionFilter);
      }
    }
    if (userFilter !== 'all') {
      data = data.filter(e => e.user_name === userFilter);
    }
    if (dateFilter !== 'all') {
      const now = new Date();
      const cutoff = new Date();
      switch (dateFilter) {
        case '1h': cutoff.setHours(now.getHours() - 1); break;
        case '24h': cutoff.setDate(now.getDate() - 1); break;
        case '7d': cutoff.setDate(now.getDate() - 7); break;
        case '30d': cutoff.setDate(now.getDate() - 30); break;
        default: break;
      }
      data = data.filter(e => new Date(e.timestamp) >= cutoff);
    }
    if (searchText.trim()) {
      const s = searchText.toLowerCase();
      data = data.filter(e =>
        (e.action || '').toLowerCase().includes(s) ||
        (e.details || '').toLowerCase().includes(s) ||
        (e.user_name || '').toLowerCase().includes(s) ||
        (e.resource_type || '').toLowerCase().includes(s)
      );
    }
    return sortDir === 'desc' ? data : [...data].reverse();
  }, [auditLog, actionFilter, userFilter, dateFilter, searchText, sortDir]);

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Stats
  const stats = useMemo(() => {
    const now = new Date();
    const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const recent = auditLog.filter(e => new Date(e.timestamp) >= last24h);
    return {
      total: auditLog.length,
      last24h: recent.length,
      denials: recent.filter(e => e.action === 'ACCESS_DENIED').length,
      emergencies: auditLog.filter(e => e.action === 'BREAK_THE_GLASS').length,
      failedLogins: recent.filter(e => e.action === 'LOGIN_FAILED').length,
    };
  }, [auditLog]);

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const getActionColor = (action) => ACTION_COLORS[action] || '#6b7280';

  return (
    <div className="page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ ...NEU.statIcon, background: `linear-gradient(145deg, ${NEU.tealLight}, ${NEU.tealDark})`, color: '#fff' }}>
            <ShieldCheck size={22} />
          </div>
          <div>
            <h1 className="page-title" style={{ margin: 0 }}>Admin Audit Trail</h1>
            <p style={{ margin: 0, fontSize: 13, color: '#6B7280' }}>
              HIPAA compliance monitoring &bull; Who changed what, when, and why
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <PrintButton pageTitle="Admin Audit Trail" />
          <button className="btn-ghost btn-sm" onClick={fetchData}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', fontSize: 13 }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total Events', value: stats.total, color: NEU.teal, icon: ShieldCheck },
          { label: 'Last 24h', value: stats.last24h, color: '#2563eb', icon: Clock },
          { label: 'Access Denials', value: stats.denials, color: '#dc2626', icon: Shield },
          { label: 'Failed Logins', value: stats.failedLogins, color: '#d97706', icon: AlertTriangle },
          { label: 'Emergency Access', value: stats.emergencies, color: '#ea580c', icon: AlertTriangle },
        ].map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={i} style={{ ...NEU.cardSm, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ ...NEU.statIcon, width: 36, height: 36, color: stat.color }}>
                  <Icon size={18} />
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#1f2937' }}>{stat.value}</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>{stat.label}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {[
          { key: 'audit', label: 'Full Audit Log', count: filteredLog.length },
          { key: 'logins', label: 'Login History', count: loginHistory.length },
          { key: 'phi', label: 'PHI Access Log', count: phiLog.length },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 20px', border: 'none', borderRadius: '8px 8px 0 0', cursor: 'pointer',
              fontWeight: 600, fontSize: 13, transition: 'all 0.15s',
              ...(activeTab === tab.key
                ? { background: `linear-gradient(145deg, ${NEU.tealLight}, ${NEU.tealDark})`, color: '#fff' }
                : { background: NEU.bg, color: '#6b7280', boxShadow: NEU.shadow.xs }),
            }}>
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Full Audit Log Tab */}
      {activeTab === 'audit' && (
        <>
          {/* Filters */}
          <div style={{ ...NEU.card, padding: 16, marginBottom: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto auto', gap: 12, alignItems: 'center' }}>
              <div style={{ position: 'relative' }}>
                <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <input type="text" placeholder="Search events..."
                  value={searchText} onChange={e => setSearchText(e.target.value)}
                  style={{ ...NEU.input, width: '100%', padding: '8px 12px 8px 36px', fontSize: 13 }} />
              </div>
              <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
                style={{ ...NEU.input, padding: '8px 12px', fontSize: 13, cursor: 'pointer' }}>
                <option value="all">All Actions</option>
                <optgroup label="Categories">
                  {Object.keys(ACTION_CATEGORIES).map(cat =>
                    <option key={cat} value={cat}>{cat}</option>
                  )}
                </optgroup>
                <optgroup label="Specific Actions">
                  {uniqueActions.map(a =>
                    <option key={a} value={a}>{a}</option>
                  )}
                </optgroup>
              </select>
              <select value={userFilter} onChange={e => setUserFilter(e.target.value)}
                style={{ ...NEU.input, padding: '8px 12px', fontSize: 13, cursor: 'pointer' }}>
                <option value="all">All Users</option>
                {uniqueUsers.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
              <select value={dateFilter} onChange={e => setDateFilter(e.target.value)}
                style={{ ...NEU.input, padding: '8px 12px', fontSize: 13, cursor: 'pointer' }}>
                <option value="all">All Time</option>
                <option value="1h">Last Hour</option>
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
              </select>
              <button className="btn-ghost btn-sm" onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 12px', fontSize: 12 }}>
                {sortDir === 'desc' ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                {sortDir === 'desc' ? 'Newest' : 'Oldest'}
              </button>
            </div>
          </div>

          {/* Audit Table */}
          <div style={NEU.tableWrap}>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#6B7280' }}>Loading audit trail...</div>
            ) : filteredLog.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
                <ShieldCheck size={40} style={{ color: '#d1d5db', marginBottom: 12 }} />
                <p style={{ margin: 0 }}>No audit events match your filters</p>
              </div>
            ) : (
              <div className="responsive-scroll">
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                <thead>
                  <tr>
                    <th style={{ ...NEU.th, padding: '10px 12px', textAlign: 'left', width: 160 }}>TIMESTAMP</th>
                    <th style={{ ...NEU.th, padding: '10px 12px', textAlign: 'left', width: 140 }}>USER</th>
                    <th style={{ ...NEU.th, padding: '10px 12px', textAlign: 'left', width: 180 }}>ACTION</th>
                    <th style={{ ...NEU.th, padding: '10px 12px', textAlign: 'left', width: 100 }}>RESOURCE</th>
                    <th style={{ ...NEU.th, padding: '10px 12px', textAlign: 'left' }}>DETAILS</th>
                    <th style={{ ...NEU.th, padding: '10px 12px', textAlign: 'center', width: 60 }}>PHI</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLog.map((event, idx) => {
                    const isExpanded = expandedRows.has(event.id || idx);
                    const severity = SEVERITY[event.action];
                    const rowBg = severity === 'critical' ? '#fef2f2'
                      : severity === 'warning' ? '#fffbeb' : '';
                    return (
                      <React.Fragment key={event.id || idx}>
                        <tr onClick={() => toggleRow(event.id || idx)}
                          style={{ cursor: 'pointer', background: rowBg, transition: 'background 0.15s' }}
                          onMouseEnter={e => { if (!rowBg) e.currentTarget.style.background = '#f9fafb'; }}
                          onMouseLeave={e => { if (!rowBg) e.currentTarget.style.background = ''; }}>
                          <td style={{ ...NEU.td, padding: '10px 12px', fontSize: 12, color: '#6b7280', fontFamily: 'monospace' }}>
                            {formatTimestamp(event.timestamp)}
                          </td>
                          <td style={{ ...NEU.td, padding: '10px 12px', fontSize: 13, fontWeight: 500 }}>
                            {event.user_name || 'System'}
                          </td>
                          <td style={{ ...NEU.td, padding: '10px 12px' }}>
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                              fontSize: 11, fontWeight: 700, fontFamily: 'monospace',
                              color: getActionColor(event.action),
                              background: `${getActionColor(event.action)}15`,
                            }}>
                              {event.action}
                            </span>
                          </td>
                          <td style={{ ...NEU.td, padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>
                            {event.resource_type || '—'}
                            {event.resource_id ? ` #${event.resource_id}` : ''}
                          </td>
                          <td style={{ ...NEU.td, padding: '10px 12px', fontSize: 12, color: '#4b5563',
                            maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {event.details || '—'}
                          </td>
                          <td style={{ ...NEU.td, padding: '10px 12px', textAlign: 'center' }}>
                            {event.phi_accessed ? (
                              <span style={{ color: '#dc2626', fontWeight: 700, fontSize: 11 }}>YES</span>
                            ) : (
                              <span style={{ color: '#d1d5db', fontSize: 11 }}>—</span>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={6} style={{ padding: '12px 24px', background: '#f9fafb' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, fontSize: 12 }}>
                                <div>
                                  <div style={{ fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Full Details</div>
                                  <div style={{ color: '#1f2937', wordBreak: 'break-word' }}>{event.details || 'No details recorded'}</div>
                                </div>
                                <div>
                                  <div style={{ fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>IP Address</div>
                                  <div style={{ fontFamily: 'monospace' }}>{event.ip_address || 'N/A'}</div>
                                </div>
                                <div>
                                  <div style={{ fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Emergency Access</div>
                                  <div>{event.emergency_access ? 'YES — Break-the-Glass active' : 'No'}</div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Login History Tab */}
      {activeTab === 'logins' && (
        <div style={NEU.tableWrap}>
          {loginHistory.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
              <LogIn size={40} style={{ color: '#d1d5db', marginBottom: 12 }} />
              <p style={{ margin: 0 }}>No login events found</p>
            </div>
          ) : (
            <div className="responsive-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead>
                <tr>
                  <th style={{ ...NEU.th, padding: '10px 12px', textAlign: 'left' }}>TIMESTAMP</th>
                  <th style={{ ...NEU.th, padding: '10px 12px', textAlign: 'left' }}>USER</th>
                  <th style={{ ...NEU.th, padding: '10px 12px', textAlign: 'left' }}>EVENT</th>
                  <th style={{ ...NEU.th, padding: '10px 12px', textAlign: 'left' }}>IP ADDRESS</th>
                  <th style={{ ...NEU.th, padding: '10px 12px', textAlign: 'left' }}>DETAILS</th>
                </tr>
              </thead>
              <tbody>
                {loginHistory.map((event, idx) => (
                  <tr key={idx} style={{
                    background: event.action === 'LOGIN_FAILED' ? '#fffbeb' : '',
                  }}>
                    <td style={{ ...NEU.td, padding: '10px 12px', fontSize: 12, fontFamily: 'monospace', color: '#6b7280' }}>
                      {formatTimestamp(event.timestamp)}
                    </td>
                    <td style={{ ...NEU.td, padding: '10px 12px', fontSize: 13, fontWeight: 500 }}>
                      {event.user_name || 'Unknown'}
                    </td>
                    <td style={{ ...NEU.td, padding: '10px 12px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                        color: getActionColor(event.action),
                        background: `${getActionColor(event.action)}15`,
                      }}>
                        {event.action === 'LOGIN_SUCCESS' && <LogIn size={12} />}
                        {event.action === 'LOGOUT' && <LogOut size={12} />}
                        {event.action === 'LOGIN_FAILED' && <AlertTriangle size={12} />}
                        {event.action === 'SESSION_TIMEOUT' && <Clock size={12} />}
                        {event.action}
                      </span>
                    </td>
                    <td style={{ ...NEU.td, padding: '10px 12px', fontSize: 12, fontFamily: 'monospace', color: '#6b7280' }}>
                      {event.ip_address || 'N/A'}
                    </td>
                    <td style={{ ...NEU.td, padding: '10px 12px', fontSize: 12, color: '#4b5563' }}>
                      {event.details || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      {/* PHI Access Log Tab */}
      {activeTab === 'phi' && (
        <div style={NEU.tableWrap}>
          {phiLog.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
              <Shield size={40} style={{ color: '#d1d5db', marginBottom: 12 }} />
              <p style={{ margin: 0 }}>No PHI access events found</p>
            </div>
          ) : (
            <div className="responsive-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead>
                <tr>
                  <th style={{ ...NEU.th, padding: '10px 12px', textAlign: 'left' }}>TIMESTAMP</th>
                  <th style={{ ...NEU.th, padding: '10px 12px', textAlign: 'left' }}>USER</th>
                  <th style={{ ...NEU.th, padding: '10px 12px', textAlign: 'left' }}>ACTION</th>
                  <th style={{ ...NEU.th, padding: '10px 12px', textAlign: 'left' }}>PATIENT</th>
                  <th style={{ ...NEU.th, padding: '10px 12px', textAlign: 'left' }}>RESOURCE</th>
                  <th style={{ ...NEU.th, padding: '10px 12px', textAlign: 'left' }}>FIELDS</th>
                </tr>
              </thead>
              <tbody>
                {phiLog.map((event, idx) => (
                  <tr key={idx}>
                    <td style={{ ...NEU.td, padding: '10px 12px', fontSize: 12, fontFamily: 'monospace', color: '#6b7280' }}>
                      {formatTimestamp(event.timestamp)}
                    </td>
                    <td style={{ ...NEU.td, padding: '10px 12px', fontSize: 13, fontWeight: 500 }}>
                      {event.user_name || 'Unknown'}
                    </td>
                    <td style={{ ...NEU.td, padding: '10px 12px', fontSize: 12 }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                        fontFamily: 'monospace', color: '#7c3aed', background: '#f5f3ff',
                      }}>
                        {event.action}
                      </span>
                    </td>
                    <td style={{ ...NEU.td, padding: '10px 12px', fontSize: 13 }}>
                      {event.last_name ? `${event.last_name}, ${event.first_name}` : '—'}
                      {event.mrn && <span style={{ fontSize: 10, color: '#9ca3af', marginLeft: 6 }}>MRN: {event.mrn}</span>}
                    </td>
                    <td style={{ ...NEU.td, padding: '10px 12px', fontSize: 12, color: '#6b7280' }}>
                      {event.resource_type || '—'}
                    </td>
                    <td style={{ ...NEU.td, padding: '10px 12px', fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>
                      {event.fields_accessed || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
